'use client';

import {
    AlertCircle,
    Check,
    Gauge,
    LoaderCircle,
    Mic,
    RotateCcw,
    Sparkles,
    Square,
    Volume2,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    getQuickReadAloudPrompt,
    submitQuickReadAloud,
    type QuickReadAloudMark,
    type QuickReadAloudSubmission,
} from '../services/quick-read-aloud';

const MAX_SECONDS = 60;
const BAR_COUNT = 30;

type RecorderState = 'idle' | 'recording' | 'stopping' | 'recorded' | 'submitting';
type MicState = 'idle' | 'checking' | 'ready' | 'error';

function messageFrom(error: unknown, fallback: string) {
    return error instanceof Error && error.message.trim() ? error.message : fallback;
}

function formatTime(seconds: number) {
    const value = Math.max(0, Math.floor(seconds));
    return String(Math.floor(value / 60)).padStart(2, '0') + ':' +
        String(value % 60).padStart(2, '0');
}

function supportedMimeType() {
    if (typeof MediaRecorder === 'undefined') return '';
    return [
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/webm',
    ].find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}

function extensionFor(type: string) {
    if (type.includes('mp4')) return 'm4a';
    if (type.includes('ogg')) return 'ogg';
    return 'webm';
}

function scoreText(mark?: QuickReadAloudMark) {
    if (!mark) return '— / 90';

    const scoreOutOf90 = mark.scoreOutOf90 ??
        (mark.outOf > 0 ? mark.score / mark.outOf * 90 : 0);

    return Math.round(Math.max(0, Math.min(90, scoreOutOf90))) + ' / 90';
}

function findMark(marks: QuickReadAloudMark[], title: string) {
    return marks.find((mark) => mark.title.toLowerCase() === title.toLowerCase());
}

function stopTracks(stream: MediaStream | null) {
    stream?.getTracks().forEach((track) => track.stop());
}

function startMeter(stream: MediaStream, update: (value: number) => void) {
    const AudioContextClass = window.AudioContext ??
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return () => undefined;

    const context = new AudioContextClass();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    const data = new Uint8Array(128);
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.72;
    source.connect(analyser);

    let frame = 0;
    let lastUpdate = 0;
    const tick = (timestamp: number) => {
        analyser.getByteTimeDomainData(data);
        let total = 0;
        data.forEach((sample) => {
            const normalized = (sample - 128) / 128;
            total += normalized * normalized;
        });
        if (timestamp - lastUpdate > 45) {
            update(Math.min(1, Math.sqrt(total / data.length) * 5.5));
            lastUpdate = timestamp;
        }
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
        cancelAnimationFrame(frame);
        source.disconnect();
        analyser.disconnect();
        void context.close();
    };
}

function LevelMeter({ level, active }: { level: number; active: boolean }) {
    return (
        <div className="flex h-14 items-center justify-center gap-1 overflow-hidden" aria-label="Microphone level">
            {Array.from({ length: BAR_COUNT }, (_, index) => {
                const wave = 0.25 + Math.abs(Math.sin(index * 0.72)) * 0.5;
                const height = Math.max(4, Math.min(46, 4 + (active ? Math.max(level, 0.1) : 0.05) * wave * 58));
                return (
                    <span
                        key={index}
                        className={'w-1 rounded-full transition-[height] duration-75 ' +
                            (active ? 'bg-secondary' : 'bg-stone-300')}
                        style={{ height }}
                    />
                );
            })}
        </div>
    );
}

function ScoreCard({
    label,
    description,
    mark,
}: {
    label: string;
    description: string;
    mark?: QuickReadAloudMark;
}) {
    const percentage = mark?.outOf ? Math.max(0, Math.min(100, mark.score / mark.outOf * 100)) : 0;
    return (
        <div className="rounded-[26px] border border-secondary/15 bg-gradient-to-br from-white to-pink-50/70 p-5 shadow-[0_16px_45px_rgba(131,24,67,0.08)]">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="font-bold text-stone-950">{label}</p>
                    <p className="mt-1 text-sm leading-5 text-stone-500">{description}</p>
                </div>
                <span className="shrink-0 rounded-full border border-secondary/15 bg-white px-3 py-1.5 text-base font-extrabold tabular-nums text-secondary shadow-sm">
                    {scoreText(mark)}
                </span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-stone-100">
                <div
                    className="h-full rounded-full bg-gradient-to-r from-secondary to-fuchsia-400"
                    style={{ width: String(percentage) + '%' }}
                />
            </div>
        </div>
    );
}

export default function QuickReadAloudPractice() {
    const [prompt, setPrompt] = useState<string | null>(null);
    const [promptLoading, setPromptLoading] = useState(true);
    const [promptError, setPromptError] = useState<string | null>(null);
    const [recorderState, setRecorderState] = useState<RecorderState>('idle');
    const [recorderError, setRecorderError] = useState<string | null>(null);
    const [seconds, setSeconds] = useState(0);
    const [level, setLevel] = useState(0);
    const [file, setFile] = useState<File | null>(null);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [duration, setDuration] = useState(0);
    const [result, setResult] = useState<QuickReadAloudSubmission | null>(null);
    const [micOpen, setMicOpen] = useState(false);
    const [micState, setMicState] = useState<MicState>('idle');
    const [micError, setMicError] = useState<string | null>(null);
    const [micName, setMicName] = useState('Default microphone');

    const recorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const meterStopRef = useRef<(() => void) | null>(null);
    const timerRef = useRef<number | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const startedAtRef = useRef(0);
    const recorderSectionRef = useRef<HTMLDivElement | null>(null);
    const resultSectionRef = useRef<HTMLDivElement | null>(null);

    const releaseMedia = useCallback(() => {
        if (timerRef.current != null) window.clearInterval(timerRef.current);
        timerRef.current = null;
        stopTracks(streamRef.current);
        streamRef.current = null;
        meterStopRef.current?.();
        meterStopRef.current = null;
        setLevel(0);
    }, []);

    const loadPrompt = useCallback(async () => {
        setPromptLoading(true);
        setPromptError(null);
        try {
            const response = await getQuickReadAloudPrompt();
            const paragraph = response.paragraph?.trim();
            if (!paragraph) throw new Error('No speaking prompt is available right now.');
            setPrompt(paragraph);
        } catch (error) {
            setPrompt(null);
            setPromptError(messageFrom(error, 'Could not load the speaking prompt.'));
        } finally {
            setPromptLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadPrompt();
    }, [loadPrompt]);

    useEffect(() => {
        if (!audioUrl) return;
        return () => URL.revokeObjectURL(audioUrl);
    }, [audioUrl]);

    useEffect(() => {
        if (!result) return;
        const frame = window.requestAnimationFrame(() => {
            resultSectionRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [result]);

    useEffect(() => {
        return () => {
            const recorder = recorderRef.current;
            if (recorder?.state === 'recording') {
                recorder.ondataavailable = null;
                recorder.onstop = null;
                recorder.stop();
            }
            stopTracks(streamRef.current);
            meterStopRef.current?.();
            if (timerRef.current != null) window.clearInterval(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!micOpen) return;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                releaseMedia();
                setMicState('idle');
                setMicError(null);
                setMicOpen(false);
            }
        };
        addEventListener('keydown', closeOnEscape);
        return () => removeEventListener('keydown', closeOnEscape);
    }, [micOpen, releaseMedia]);

    const closeMic = useCallback(() => {
        releaseMedia();
        setMicState('idle');
        setMicError(null);
        setMicOpen(false);
    }, [releaseMedia]);

    const testMic = useCallback(async () => {
        releaseMedia();
        setMicState('checking');
        setMicError(null);
        try {
            if (!navigator.mediaDevices?.getUserMedia) throw new Error('Microphone testing is not supported in this browser.');
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            setMicName(stream.getAudioTracks()[0]?.label || 'Connected microphone');
            meterStopRef.current = startMeter(stream, setLevel);
            setMicState('ready');
        } catch (error) {
            setMicState('error');
            setMicError(messageFrom(error, 'Microphone access is unavailable.'));
        }
    }, [releaseMedia]);

    const stopRecording = useCallback(() => {
        if (recorderRef.current?.state !== 'recording') return;
        setRecorderState('stopping');
        recorderRef.current.stop();
    }, []);

    const startRecording = useCallback(async () => {
        releaseMedia();
        setRecorderError(null);
        setResult(null);
        setFile(null);
        setAudioUrl(null);
        setDuration(0);
        setSeconds(0);

        try {
            if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
                throw new Error('Audio recording is not supported in this browser.');
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            streamRef.current = stream;
            const preferredType = supportedMimeType();
            const recorder = preferredType
                ? new MediaRecorder(stream, { mimeType: preferredType })
                : new MediaRecorder(stream);
            recorderRef.current = recorder;
            chunksRef.current = [];
            startedAtRef.current = performance.now();

            recorder.ondataavailable = (event) => {
                if (event.data.size) chunksRef.current.push(event.data);
            };
            recorder.onerror = () => {
                releaseMedia();
                setRecorderState('idle');
                setRecorderError('The microphone became unavailable. Please check it and record again.');
            };
            recorder.onstop = () => {
                const recordedSeconds = Math.max(0.1, (performance.now() - startedAtRef.current) / 1000);
                const type = recorder.mimeType || preferredType || 'audio/webm';
                const blob = new Blob(chunksRef.current, { type });
                releaseMedia();
                recorderRef.current = null;

                if (!blob.size) {
                    setRecorderState('idle');
                    setRecorderError('No audio was captured. Check your microphone and record again.');
                    return;
                }
                setFile(new File([blob], 'pte-ultra-read-aloud.' + extensionFor(type), { type }));
                setAudioUrl(URL.createObjectURL(blob));
                setDuration(recordedSeconds);
                setSeconds(recordedSeconds);
                setRecorderState('recorded');
            };

            meterStopRef.current = startMeter(stream, setLevel);
            recorder.start(250);
            setRecorderState('recording');
            timerRef.current = window.setInterval(() => {
                const elapsed = (performance.now() - startedAtRef.current) / 1000;
                setSeconds(elapsed);
                if (elapsed >= MAX_SECONDS && recorder.state === 'recording') {
                    setRecorderState('stopping');
                    recorder.stop();
                }
            }, 200);
        } catch (error) {
            releaseMedia();
            setRecorderState('idle');
            setRecorderError(messageFrom(error, 'Could not start recording. Check microphone access and retry.'));
        }
    }, [releaseMedia]);

    const analyse = useCallback(async () => {
        if (!file || !duration) return;
        setRecorderError(null);
        setRecorderState('submitting');
        try {
            setResult(await submitQuickReadAloud(file, duration));
            setRecorderState('recorded');
        } catch (error) {
            setRecorderState('recorded');
            setRecorderError(messageFrom(error, 'We could not analyse your recording. Please retry.'));
        }
    }, [duration, file]);

    const marks = useMemo(
        () => result?.result.markTableResult?.filter(
            (mark) => Number.isFinite(mark.score) && Number.isFinite(mark.outOf),
        ) ?? [],
        [result],
    );
    const transcript = result?.result.transcript?.trim() ||
        result?.result.oralFluencyReview?.transcript?.trim();
    const tokens = result?.result.oralFluencyReview?.tokenResults ?? [];

    return (
        <div className="mt-10 rounded-[32px] border border-stone-200/80 bg-white/90 p-4 shadow-[0_28px_90px_rgba(28,25,23,0.10)] sm:p-7 lg:p-9">
            <div className="flex flex-col gap-4 border-b border-stone-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="max-w-2xl">
                    <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-secondary">
                        <Sparkles className="size-4" /> Try it now
                    </div>
                    <h3 className="mt-3 text-[1.65rem] font-semibold tracking-tight text-stone-950 sm:text-[2rem]">
                        Get your Read Aloud analysis
                    </h3>
                    <p className="mt-2 leading-7 text-stone-600">
                        Read the prompt, record your response, and see instant AI feedback. No account needed.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setMicOpen(true)}
                    disabled={recorderState === 'recording' || recorderState === 'stopping'}
                    className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-secondary/25 bg-secondary/8 px-5 text-sm font-bold text-secondary transition hover:bg-secondary/12 disabled:opacity-50"
                >
                    <Volume2 className="size-4" /> Check your microphone
                </button>
            </div>

            <div className="mt-6 rounded-[24px] border border-stone-200 bg-stone-50 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                    <span className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.13em] text-secondary">
                        Read aloud
                    </span>
                    <span className="text-xs font-semibold text-stone-500">Up to {MAX_SECONDS} seconds</span>
                </div>
                {promptLoading ? (
                    <div className="flex min-h-32 items-center justify-center gap-2 text-stone-500" role="status">
                        <LoaderCircle className="size-5 animate-spin text-secondary" /> Loading your prompt…
                    </div>
                ) : promptError ? (
                    <div className="flex min-h-32 flex-col items-center justify-center gap-4 text-center" role="alert">
                        <p className="flex items-center gap-2 text-stone-700">
                            <AlertCircle className="size-5 text-secondary" /> {promptError}
                        </p>
                        <button
                            type="button"
                            onClick={() => void loadPrompt()}
                            className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-bold"
                        >
                            <RotateCcw className="size-4" /> Retry
                        </button>
                    </div>
                ) : (
                    <p className="mt-5 text-[1.08rem] font-medium leading-8 text-stone-800 sm:text-[1.18rem] sm:leading-9">
                        {prompt}
                    </p>
                )}
            </div>

            <div id="quick-read-aloud-recorder" ref={recorderSectionRef} className="mt-5 scroll-mt-24 rounded-[24px] border border-stone-200 bg-white p-4 sm:p-5">
                <div className="flex items-center gap-3">
                    <span className={'grid size-11 shrink-0 place-items-center rounded-full border ' +
                        (recorderState === 'recording'
                            ? 'border-red-200 bg-red-50 text-red-500'
                            : 'border-stone-200 bg-stone-50 text-stone-500')}>
                        <Mic className="size-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                            <p className="font-bold text-stone-900" aria-live="polite">
                                {recorderState === 'recording' && 'Recording…'}
                                {recorderState === 'stopping' && 'Preparing recording…'}
                                {recorderState === 'recorded' && 'Recording ready'}
                                {recorderState === 'submitting' && 'Analysing your speaking…'}
                                {recorderState === 'idle' && 'Ready to record'}
                            </p>
                            <span className="font-mono text-sm font-bold tabular-nums text-stone-600">
                                {formatTime(seconds)}
                            </span>
                        </div>
                        <LevelMeter level={level} active={recorderState === 'recording'} />
                    </div>
                </div>

                {audioUrl && recorderState !== 'recording' && recorderState !== 'stopping' && (
                    <audio className="mt-3 w-full" controls preload="metadata" src={audioUrl}>
                        Your browser does not support audio playback.
                    </audio>
                )}
                {recorderError && (
                    <p className="mt-4 flex items-start gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm leading-6 text-red-700" role="alert">
                        <AlertCircle className="mt-1 size-4 shrink-0" /> {recorderError}
                    </p>
                )}

                <div className="mt-4 flex flex-wrap gap-3">
                    {recorderState === 'recording' ? (
                        <button
                            type="button"
                            onClick={stopRecording}
                            className="inline-flex min-h-12 items-center gap-2 rounded-full bg-stone-950 px-6 font-bold text-white"
                        >
                            <Square className="size-4 fill-current" /> Stop recording
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => void startRecording()}
                            disabled={!prompt || recorderState === 'stopping' || recorderState === 'submitting'}
                            className="inline-flex min-h-12 items-center gap-2 rounded-full bg-secondary px-6 font-bold text-white shadow-[0_14px_34px_rgba(236,72,153,0.25)] disabled:opacity-50"
                        >
                            {file ? <RotateCcw className="size-4" /> : <Mic className="size-4" />}
                            {file ? 'Record again' : 'Start recording'}
                        </button>
                    )}
                    {file && recorderState !== 'recording' && recorderState !== 'stopping' && (
                        <button
                            type="button"
                            onClick={() => void analyse()}
                            disabled={recorderState === 'submitting'}
                            className="inline-flex min-h-12 items-center gap-2 rounded-full bg-stone-950 px-6 font-bold text-white disabled:opacity-70"
                        >
                            {recorderState === 'submitting'
                                ? <LoaderCircle className="size-4 animate-spin" />
                                : <Sparkles className="size-4" />}
                            {recorderState === 'submitting' ? 'Analysing…' : 'Analyse my speaking'}
                        </button>
                    )}
                </div>
                <p className="mt-4 text-xs leading-5 text-stone-500">
                    Your recording is sent directly from this browser to the PTE Ultra analysis API. Maximum file size: 10 MB.
                </p>
            </div>

            {result && (
                <div id="quick-read-aloud-result" ref={resultSectionRef} className="mt-7 scroll-mt-24 rounded-[30px] border border-secondary/15 bg-gradient-to-b from-pink-50/80 via-white to-white p-5 shadow-[0_24px_70px_rgba(131,24,67,0.10)] sm:p-7">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <p className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                                <Check className="size-4" /> Analysis complete
                            </p>
                            <h3 className="mt-2 text-[1.65rem] font-semibold text-stone-950">Your speaking result</h3>
                        </div>
                        <div className="flex items-center gap-3 rounded-[24px] border border-secondary/20 bg-white px-5 py-4 shadow-[0_12px_35px_rgba(131,24,67,0.10)]">
                            <span className="grid size-11 shrink-0 place-items-center rounded-full bg-secondary/10">
                                <Gauge className="size-6 text-secondary" />
                            </span>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.12em] text-stone-500">Overall</p>
                                <p className="mt-0.5 text-2xl font-extrabold tabular-nums text-stone-950">{scoreText(findMark(marks, 'Total'))}</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 grid gap-4 md:grid-cols-3">
                        <ScoreCard label="Content" description="Words matched to the prompt" mark={findMark(marks, 'Content')} />
                        <ScoreCard label="Fluency" description="Rhythm, pace, and smooth delivery" mark={findMark(marks, 'Fluency')} />
                        <ScoreCard label="Pronunciation" description="Clarity of spoken sounds" mark={findMark(marks, 'Pronunciation')} />
                    </div>

                    {tokens.length > 0 && (
                        <div className="mt-5 rounded-[24px] border border-stone-200 bg-stone-50 p-5">
                            <h4 className="font-bold text-stone-950">Word clarity</h4>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {tokens.map((token, index) => (
                                    <span
                                        key={token.token + '-' + index}
                                        className={'rounded-lg border px-2.5 py-1 text-sm font-semibold ' +
                                            (token.grade === 'High'
                                                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                                : token.grade === 'Medium'
                                                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                                                    : 'border-red-200 bg-red-50 text-red-700')}
                                    >
                                        {token.token}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {transcript && (
                        <div className="mt-5 rounded-[24px] border border-stone-200 bg-white p-5">
                            <h4 className="font-bold text-stone-950">Your spoken text</h4>
                            <p className="mt-3 leading-7 text-stone-600">“{transcript}”</p>
                        </div>
                    )}

                    {result.feedback && (
                        <div className="mt-5 rounded-[24px] border border-secondary/20 bg-secondary/[0.055] p-5 sm:p-6">
                            <p className="flex items-center gap-2 font-bold text-stone-950">
                                <Sparkles className="size-5 text-secondary" /> Your next improvement
                            </p>
                            <p className="mt-3 text-lg font-semibold leading-7 text-stone-900">
                                {result.feedback.focusNow || result.feedback.summary}
                            </p>
                            {result.feedback.summary && result.feedback.summary !== result.feedback.focusNow && (
                                <p className="mt-2 leading-7 text-stone-600">{result.feedback.summary}</p>
                            )}
                            {result.feedback.nextAttemptFocus && (
                                <p className="mt-4 rounded-2xl bg-white/80 px-4 py-3 text-sm font-semibold leading-6 text-stone-700">
                                    Next attempt: {result.feedback.nextAttemptFocus}
                                </p>
                            )}
                            {result.feedback.practiceTargets?.length > 0 && (
                                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                                    {result.feedback.practiceTargets.map((target) => (
                                        <li key={target} className="flex items-start gap-2 text-sm leading-6 text-stone-700">
                                            <Check className="mt-1 size-4 shrink-0 text-emerald-600" /> {target}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <div className="mt-5 flex flex-col items-start gap-2 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm leading-6 text-stone-500">
                            Ready to improve your score? Record a new response and submit it for review.
                        </p>
                        <button
                            type="button"
                            onClick={() => recorderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-secondary px-5 font-bold text-white shadow-[0_12px_30px_rgba(236,72,153,0.25)]"
                        >
                            <RotateCcw className="size-4" /> Try again
                        </button>
                    </div>
                </div>
            )}

            {micOpen && (
                <div
                    className="fixed inset-0 z-[100] flex items-end justify-center bg-stone-950/55 backdrop-blur-sm sm:items-center sm:p-5"
                    role="presentation"
                    onMouseDown={(event) => event.currentTarget === event.target && closeMic()}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="mic-title"
                        className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-t-[30px] bg-white p-5 shadow-2xl sm:rounded-[30px] sm:p-7"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 id="mic-title" className="text-xl font-bold text-stone-950">Microphone setup</h3>
                                <p className="mt-1 text-sm leading-6 text-stone-500">
                                    Connect, allow access, then speak to see the level move.
                                </p>
                            </div>
                            <button type="button" onClick={closeMic} aria-label="Close microphone setup" className="grid size-10 place-items-center rounded-full hover:bg-stone-100">
                                <X className="size-5" />
                            </button>
                        </div>
                        <ol className="mt-5 grid gap-3">
                            {[
                                'Connect your headset or microphone and select it as your computer input.',
                                'Choose Allow when this browser asks for microphone access.',
                                'Speak normally. Moving bars mean your microphone is ready.',
                            ].map((instruction, index) => (
                                <li key={instruction} className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-4 text-sm leading-6 text-stone-700">
                                    <span className="grid size-7 shrink-0 place-items-center rounded-full bg-secondary/10 text-xs font-bold text-secondary">
                                        {index + 1}
                                    </span>
                                    {instruction}
                                </li>
                            ))}
                        </ol>
                        <div className="mt-5 rounded-[22px] border border-stone-200 p-4">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <p className="font-bold text-stone-900">
                                        {micState === 'ready' ? 'Microphone connected' : 'Test your microphone'}
                                    </p>
                                    <p className="mt-1 text-xs text-stone-500">
                                        {micState === 'ready' ? micName : 'Test audio stays in your browser.'}
                                    </p>
                                </div>
                                {micState === 'ready' && (
                                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">Ready</span>
                                )}
                            </div>
                            <div className="mt-3 rounded-2xl bg-stone-50 px-3">
                                <LevelMeter level={level} active={micState === 'ready'} />
                            </div>
                            {micError && (
                                <p className="mt-3 rounded-2xl bg-red-50 px-4 py-3 text-sm leading-6 text-red-700" role="alert">
                                    {micError} Open the site controls beside the address bar, set Microphone to Allow, then refresh.
                                </p>
                            )}
                            <div className="mt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => void testMic()}
                                    disabled={micState === 'checking'}
                                    className="inline-flex min-h-11 items-center gap-2 rounded-full bg-secondary px-5 text-sm font-bold text-white disabled:opacity-70"
                                >
                                    {micState === 'checking'
                                        ? <LoaderCircle className="size-4 animate-spin" />
                                        : <Mic className="size-4" />}
                                    {micState === 'ready' ? 'Test again' : micState === 'checking' ? 'Checking…' : 'Test microphone'}
                                </button>
                                {micState === 'ready' && (
                                    <button type="button" onClick={closeMic} className="rounded-full border border-stone-300 px-5 text-sm font-bold">
                                        Done
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
